import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

// Define icon components outside to avoid recreating on each render
const IconLeft = ({ className: iconClassName, ...iconProps }) => (
  <ChevronLeft className={cn("h-4 w-4", iconClassName)} {...iconProps} />
)

const IconRight = ({ className: iconClassName, ...iconProps }) => (
  <ChevronRight className={cn("h-4 w-4", iconClassName)} {...iconProps} />
)

function CalendarWithYear({
  className,
  classNames,
  showOutsideDays = true,
  selected,
  onSelect,
  fromYear = 1990,
  toYear = new Date().getFullYear() + 1,
  ...props
}) {
  const [month, setMonth] = React.useState(selected || new Date())
  
  const years = React.useMemo(() => {
    const yearList = []
    for (let year = toYear; year >= fromYear; year--) {
      yearList.push(year)
    }
    return yearList
  }, [fromYear, toYear])

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]

  const handleYearChange = (year) => {
    const newDate = new Date(month)
    newDate.setFullYear(parseInt(year))
    setMonth(newDate)
  }

  const handleMonthChange = (monthIndex) => {
    const newDate = new Date(month)
    newDate.setMonth(parseInt(monthIndex))
    setMonth(newDate)
  }

  return (
    <div className={cn("p-3", className)}>
      {/* Year and Month selectors */}
      <div className="flex gap-2 mb-4">
        <Select value={month.getFullYear().toString()} onValueChange={handleYearChange}>
          <SelectTrigger className="w-[100px] bg-black/50 border-white/10 text-white">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10 text-white max-h-[200px]">
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()} className="text-white hover:bg-white/10">
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={month.getMonth().toString()} onValueChange={handleMonthChange}>
          <SelectTrigger className="flex-1 bg-black/50 border-white/10 text-white">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
            {months.map((monthName, index) => (
              <SelectItem key={monthName} value={index.toString()} className="text-white hover:bg-white/10">
                {monthName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DayPicker
        showOutsideDays={showOutsideDays}
        month={month}
        onMonthChange={setMonth}
        mode="single"
        selected={selected}
        onSelect={onSelect}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "text-sm font-medium",
          nav: "space-x-1 flex items-center",
          nav_button: cn(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          ),
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell:
            "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
          row: "flex w-full mt-2",
          cell: cn(
            "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
            "[&:has([aria-selected])]:rounded-md"
          ),
          day: cn(
            buttonVariants({ variant: "ghost" }),
            "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
          ),
          day_range_start: "day-range-start",
          day_range_end: "day-range-end",
          day_selected:
            "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
          day_today: "bg-accent text-accent-foreground",
          day_outside:
            "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
          day_disabled: "text-muted-foreground opacity-50",
          day_range_middle:
            "aria-selected:bg-accent aria-selected:text-accent-foreground",
          day_hidden: "invisible",
          ...classNames,
        }}
        components={{
          IconLeft,
          IconRight,
        }}
        {...props}
      />
    </div>
  )
}
CalendarWithYear.displayName = "CalendarWithYear"

export { CalendarWithYear }
